// A small, dependency-free DXF writer used by "File → Export to DXF".
//
// Targets AutoCAD R12 (AC1009) — the most universally-compatible DXF version.
// R12 needs no CLASSES/OBJECTS sections, no handles, and no subclass markers, so
// the output opens reliably in essentially every CAD program (unlike a hand-rolled
// R2018 file, which strict readers reject with "invalid index" over the missing
// object dictionary). Modern entities are mapped to R12 equivalents: LWPOLYLINE →
// POLYLINE/VERTEX/SEQEND, ELLIPSE → tessellated polyline, HATCH → SOLID fills,
// MTEXT → stacked TEXT. Colours are the closest AutoCAD Color Index. Coordinates
// are millimetres.

import type { Vec2 } from './types';
import { earClip } from './triangulate';

/** A line type: CONTINUOUS (the default), DASHED or DASHDOT; each beyond
 *  CONTINUOUS is declared only when something uses it. */
export type DxfLtype = 'CONTINUOUS' | 'DASHED' | 'DASHDOT';
/** `ltype`: the entity's own line type (absent = its layer's). */
export interface DxfLine { kind: 'line'; a: Vec2; b: Vec2; layer?: string; color?: number; ltype?: DxfLtype; }
export interface DxfPolyline { kind: 'polyline'; points: Vec2[]; closed?: boolean; layer?: string; color?: number; ltype?: DxfLtype; }
export interface DxfCircle { kind: 'circle'; c: Vec2; r: number; layer?: string; color?: number; }
export interface DxfArc { kind: 'arc'; c: Vec2; r: number; startDeg: number; endDeg: number; layer?: string; color?: number; }
/** Axis endpoint is the major-axis tip relative to the centre; ratio = minor/major. */
export interface DxfEllipse { kind: 'ellipse'; c: Vec2; majorEnd: Vec2; ratio: number; startDeg?: number; endDeg?: number; layer?: string; color?: number; }
/** `align` / `baseline`: where `pos` sits on the text — absent, the left end of
 *  its baseline; otherwise its alignment point (R12 72 centre / right, 73
 *  middle / top, with 11 / 21). */
export interface DxfText { kind: 'text'; pos: Vec2; height: number; text: string; rotationDeg?: number; layer?: string; color?: number; align?: 'center' | 'right'; baseline?: 'middle' | 'top'; }
/** Multi-line text; emitted as stacked TEXT lines (R12 has no MTEXT). */
export interface DxfMText { kind: 'mtext'; pos: Vec2; height: number; text: string; width?: number; layer?: string; color?: number; /** Degrees CCW; lines step down the turned axis. */ rotation?: number; }
/** A solid-filled region bounded by closed loops (outer first, then its holes).
 *  Emitted as SOLIDs (R12 has no HATCH): ear-clipped triangles around the holes
 *  that lie inside the outer loop; with none, one quad or a triangle fan for a
 *  convex outer loop, ear-clipped triangles for any other. A later loop that is
 *  not inside the outer one (an imported plan's separate island) is left out,
 *  not bridged into it. Callers usually add an outline polyline too. */
export interface DxfHatch { kind: 'hatch'; loops: Vec2[][]; layer?: string; color?: number; }

export type DxfEntity =
  | DxfArc
  | DxfCircle
  | DxfEllipse
  | DxfHatch
  | DxfLine
  | DxfMText
  | DxfPolyline
  | DxfText;

/** `off`: switched off (a negative colour); `locked`: flag 4 (R12 "locked");
 *  `ltype`: its line type, CONTINUOUS when absent. */
export interface DxfLayer { name: string; color: number; off?: boolean; locked?: boolean; ltype?: DxfLtype; }

/** Closest AutoCAD Color Index for an #rrggbb colour (R12 has no true colour). */
const ACI_PALETTE: Array<[number, [number, number, number]]> = [
  [1, [255, 0, 0]], [2, [255, 255, 0]], [3, [0, 255, 0]], [4, [0, 255, 255]],
  [5, [0, 0, 255]], [6, [255, 0, 255]], [7, [255, 255, 255]], [8, [128, 128, 128]],
  [9, [192, 192, 192]], [30, [255, 128, 0]], [40, [255, 191, 128]], [50, [255, 255, 128]],
  [90, [128, 255, 0]], [140, [0, 255, 191]], [150, [0, 128, 255]], [200, [128, 0, 255]],
  [211, [255, 0, 128]], [250, [51, 51, 51]], [252, [102, 102, 102]], [254, [204, 204, 204]],
];

export function rgbToAci(hex: string): number | undefined {
  const m = /^#?([0-9a-f]{6})/i.exec(hex.trim());
  if (!m) return undefined;
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 0xff, g = (n >> 8) & 0xff, b = n & 0xff;
  let best = 7, bestD = Infinity;
  for (const [aci, [pr, pg, pb]] of ACI_PALETTE) {
    const d = (r - pr) ** 2 + (g - pg) ** 2 + (b - pb) ** 2;
    if (d < bestD) { bestD = d; best = aci; }
  }
  return best;
}

/** Whether a closed loop is convex: it turns one way only (a straight point, a
 *  jamb, does not count as a turn) and goes round once. Either winding. A loop
 *  that never turns (no area: a 0 mm wall's band) counts as convex — there is
 *  nothing to triangulate. */
export function isConvexLoop(loop: readonly Vec2[]): boolean {
  let ext = 0;
  for (const p of loop) ext = Math.max(ext, Math.abs(p.x - loop[0].x), Math.abs(p.y - loop[0].y));
  const near = (p: Vec2, q: Vec2): boolean => Math.hypot(p.x - q.x, p.y - q.y) <= ext * 1e-9;
  const pts: Vec2[] = [];
  for (const p of loop) if (!pts.length || !near(p, pts[pts.length - 1])) pts.push(p);
  while (pts.length > 1 && near(pts[0], pts[pts.length - 1])) pts.pop();
  const n = pts.length;
  let sign = 0, total = 0, spike = false;
  for (let i = 0; i < n; i++) {
    const a = pts[(i + n - 1) % n], b = pts[i], c = pts[(i + 1) % n];
    const ux = b.x - a.x, uy = b.y - a.y, vx = c.x - b.x, vy = c.y - b.y;
    const cr = ux * vy - uy * vx, dot = ux * vx + uy * vy;
    total += Math.atan2(cr, dot);
    if (Math.abs(cr) <= 1e-9 * Math.hypot(ux, uy) * Math.hypot(vx, vy)) { if (dot < 0) spike = true; continue; }
    const s = Math.sign(cr);
    if (sign && s !== sign) return false;
    sign = s;
  }
  if (!sign) return true;
  return !spike && Math.abs(Math.abs(total) - 2 * Math.PI) < 1e-6;
}

/** Whether `p` lies inside the closed loop (even-odd ray cast; a point on the
 *  boundary may go either way). */
function insideLoop(p: Vec2, loop: readonly Vec2[]): boolean {
  let inside = false;
  for (let i = 0, j = loop.length - 1; i < loop.length; j = i++) {
    const a = loop[i], b = loop[j];
    if ((a.y > p.y) !== (b.y > p.y) && p.x < a.x + ((p.y - a.y) * (b.x - a.x)) / (b.y - a.y)) inside = !inside;
  }
  return inside;
}

/** Build a complete DXF (R12) document string. */
export function buildDxf(entities: DxfEntity[], layers: DxfLayer[] = []): string {
  const out: string[] = [];
  const pair = (code: number, value: string | number): void => { out.push(String(code)); out.push(String(value)); };
  const num = (v: number): string => {
    if (!isFinite(v)) v = 0;
    return (Math.abs(v) < 1e-9 ? 0 : v).toFixed(6).replace(/\.?0+$/, (s) => (s.includes('.') ? '' : s));
  };
  const v2 = (cx: number, cy: number, p: Vec2): void => { pair(cx, num(p.x)); pair(cy, num(p.y)); };

  // ---- HEADER ----
  pair(0, 'SECTION'); pair(2, 'HEADER');
  pair(9, '$ACADVER'); pair(1, 'AC1009');   // AutoCAD R12
  pair(9, '$MEASUREMENT'); pair(70, 1);      // metric
  pair(0, 'ENDSEC');

  // ---- TABLES ----
  const allLayers: DxfLayer[] = [{ name: '0', color: 7 }, ...layers.filter((l) => l.name !== '0')];
  pair(0, 'SECTION'); pair(2, 'TABLES');

  // DASHED / DASHDOT are declared only when a layer or an entity uses one, so a
  // drawing without them is written exactly as before.
  const uses = (t: DxfLtype): boolean => allLayers.some((l) => l.ltype === t)
    || entities.some((e) => (e.kind === 'line' || e.kind === 'polyline') && e.ltype === t);
  const dashed = uses('DASHED'), dashdot = uses('DASHDOT');
  pair(0, 'TABLE'); pair(2, 'LTYPE'); pair(70, 1 + (dashed ? 1 : 0) + (dashdot ? 1 : 0));
  pair(0, 'LTYPE'); pair(2, 'CONTINUOUS'); pair(70, 0); pair(3, 'Solid line'); pair(72, 65); pair(73, 0); pair(40, 0);
  if (dashed) {
    pair(0, 'LTYPE'); pair(2, 'DASHED'); pair(70, 0); pair(3, 'Dashed __ __ __'); pair(72, 65); pair(73, 2); pair(40, 150);
    pair(49, 100); pair(49, -50);
  }
  if (dashdot) {
    // DASHED's 150 mm period, so it reads as dash-dot beside 300 mm arrows at model scale.
    pair(0, 'LTYPE'); pair(2, 'DASHDOT'); pair(70, 0); pair(3, 'Dash dot __ . __ .'); pair(72, 65); pair(73, 4); pair(40, 150);
    pair(49, 78); pair(49, -26); pair(49, 20); pair(49, -26);
  }
  pair(0, 'ENDTAB');

  pair(0, 'TABLE'); pair(2, 'LAYER'); pair(70, allLayers.length);
  for (const l of allLayers) {
    pair(0, 'LAYER'); pair(2, l.name); pair(70, l.locked ? 4 : 0); pair(62, l.off ? -Math.abs(l.color) : l.color); pair(6, l.ltype ?? 'CONTINUOUS');
  }
  pair(0, 'ENDTAB');

  pair(0, 'TABLE'); pair(2, 'STYLE'); pair(70, 1);
  pair(0, 'STYLE'); pair(2, 'STANDARD'); pair(70, 0); pair(40, 0); pair(41, 1); pair(50, 0); pair(71, 0); pair(42, 2.5); pair(3, 'txt'); pair(4, '');
  pair(0, 'ENDTAB');

  pair(0, 'TABLE'); pair(2, 'APPID'); pair(70, 1);
  pair(0, 'APPID'); pair(2, 'ACAD'); pair(70, 0);
  pair(0, 'ENDTAB');
  pair(0, 'ENDSEC');

  // R12 wants a BLOCKS section to exist, even if empty.
  pair(0, 'SECTION'); pair(2, 'BLOCKS'); pair(0, 'ENDSEC');

  // ---- ENTITIES ----
  const head = (type: string, layer: string, color?: number, ltype?: DxfLtype): void => {
    pair(0, type); pair(8, layer || '0');
    if (ltype) pair(6, ltype);
    if (typeof color === 'number' && color >= 0) pair(62, color);
  };
  const polyline = (points: Vec2[], closed: boolean, layer: string, color?: number, ltype?: DxfLtype): void => {
    if (points.length < 2) return;
    head('POLYLINE', layer, color, ltype); pair(66, 1); pair(70, closed ? 1 : 0);
    pair(10, 0); pair(20, 0); pair(30, 0);
    for (const p of points) { head('VERTEX', layer, color); v2(10, 20, p); pair(30, 0); }
    pair(0, 'SEQEND'); pair(8, layer || '0');
  };
  const ellipsePts = (e: DxfEllipse): Vec2[] => {
    const ax = e.majorEnd.x, ay = e.majorEnd.y;
    const bx = -ay * e.ratio, by = ax * e.ratio; // minor axis = perpendicular · ratio
    const s = ((e.startDeg ?? 0) * Math.PI) / 180, en = ((e.endDeg ?? 360) * Math.PI) / 180;
    const n = 72, pts: Vec2[] = [];
    for (let i = 0; i <= n; i++) {
      const t = s + ((en - s) * i) / n;
      pts.push({ x: e.c.x + ax * Math.cos(t) + bx * Math.sin(t), y: e.c.y + ay * Math.cos(t) + by * Math.sin(t) });
    }
    return pts;
  };
  // SOLID fills with a "bowtie" corner order (3rd and 4th points swapped); the 4th
  // repeats the 3rd for a triangle.
  const solid = (a: Vec2, b: Vec2, c: Vec2, d: Vec2, layer: string, color?: number): void => {
    head('SOLID', layer, color);
    v2(10, 20, a); pair(30, 0); v2(11, 21, b); pair(31, 0);
    v2(12, 22, d); pair(32, 0); v2(13, 23, c); pair(33, 0);
  };

  const writeEntity = (e: DxfEntity): void => {
    const layer = e.layer || '0';
    switch (e.kind) {
      case 'line':
        head('LINE', layer, e.color, e.ltype); v2(10, 20, e.a); pair(30, 0); v2(11, 21, e.b); pair(31, 0);
        break;
      case 'polyline':
        polyline(e.points, !!e.closed, layer, e.color, e.ltype);
        break;
      case 'circle':
        head('CIRCLE', layer, e.color); v2(10, 20, e.c); pair(30, 0); pair(40, num(e.r));
        break;
      case 'arc':
        head('ARC', layer, e.color); v2(10, 20, e.c); pair(30, 0); pair(40, num(e.r)); pair(50, num(e.startDeg)); pair(51, num(e.endDeg));
        break;
      case 'ellipse': {
        const pts = ellipsePts(e);
        const closed = Math.abs((e.endDeg ?? 360) - (e.startDeg ?? 0)) >= 359.9;
        polyline(pts, closed, layer, e.color);
        break;
      }
      case 'text':
        head('TEXT', layer, e.color); v2(10, 20, e.pos); pair(30, 0); pair(40, num(e.height));
        pair(1, e.text.replace(/\r?\n/g, ' ')); if (e.rotationDeg) pair(50, num(e.rotationDeg)); pair(7, 'STANDARD');
        if (e.align || e.baseline) {
          if (e.align) pair(72, e.align === 'center' ? 1 : 2);
          v2(11, 21, e.pos); pair(31, 0);
          if (e.baseline) pair(73, e.baseline === 'middle' ? 2 : 3);
        }
        break;
      case 'mtext': {
        const lines = e.text.split(/\r?\n/);
        const r = ((e.rotation ?? 0) * Math.PI) / 180;
        lines.forEach((line, i) => {
          if (!line) return;
          head('TEXT', layer, e.color);
          const d = i * e.height * 1.4; // down the text's own (turned) axis
          pair(10, num(e.pos.x + Math.sin(r) * d)); pair(20, num(e.pos.y - Math.cos(r) * d)); pair(30, 0);
          pair(40, num(e.height)); pair(1, line);
          if (e.rotation) pair(50, num(e.rotation));
          pair(7, 'STANDARD');
        });
        break;
      }
      case 'hatch': {
        const loop = e.loops[0] || [];
        const holes = e.loops.slice(1).filter((h) => h.length >= 3 && h.some((p) => insideLoop(p, loop)));
        if (holes.length) for (const [a, b, c] of earClip(loop, holes)) solid(a, b, c, c, layer, e.color);
        else if (loop.length === 3) solid(loop[0], loop[1], loop[2], loop[2], layer, e.color);
        // A fan (or one quad) only covers a CONVEX loop; anything else would fill
        // its notch or cross itself, so it goes as triangles.
        else if (loop.length >= 4 && !isConvexLoop(loop)) for (const [a, b, c] of earClip(loop)) solid(a, b, c, c, layer, e.color);
        else if (loop.length === 4) solid(loop[0], loop[1], loop[2], loop[3], layer, e.color); // one quad, no diagonal seam
        else if (loop.length > 4) for (let i = 1; i + 1 < loop.length; i++) solid(loop[0], loop[i], loop[i + 1], loop[i + 1], layer, e.color);
        break;
      }
    }
  };

  pair(0, 'SECTION'); pair(2, 'ENTITIES');
  for (const e of entities) writeEntity(e);
  pair(0, 'ENDSEC');

  pair(0, 'EOF');
  return out.join('\r\n') + '\r\n';
}
